/**
 * Automation rules screen (mobile C6).
 *
 * Lists automation rules (GET /api/automation-rules) and offers an inline "+ New rule" form
 * (name, trigger_type, channel, template_id [from templates], optional phase_name, optional
 * timing_offset) → POST /api/automation-rules.
 *
 * Pushed over the tabs on the parent (app) stack. NEVER imports @smartbox/* (invariant 1).
 * Design-system primitives only; no raw styles.
 */
import { useState } from 'react';
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listAutomationRules,
  createAutomationRule,
  listTemplates,
  type Channel,
  type AutomationRuleRow,
} from '../../../src/api/comms';
import { Screen, Text, Card, Button, Field, Select, Badge, ListRow } from '../../../src/components/ui';
import { theme, type StatusKey } from '../../../src/theme';
import { extractErrorMessage, humanize } from '../../../src/lib/format';

type TriggerType = 'phase_complete' | 'next_phase_due';

const TRIGGER_OPTIONS: ReadonlyArray<{ value: TriggerType; label: string }> = [
  { value: 'phase_complete', label: 'Phase complete' },
  { value: 'next_phase_due', label: 'Next phase due' },
];

const CHANNEL_OPTIONS: ReadonlyArray<{ value: Channel; label: string }> = [
  { value: 'email', label: 'Email' },
  { value: 'sms', label: 'SMS' },
];

function channelBadge(channel: string): { key: StatusKey; label: string } {
  return channel === 'sms'
    ? { key: 'inProgress', label: 'SMS' }
    : { key: 'sent', label: 'Email' };
}

export default function AutomationsScreen() {
  const queryClient = useQueryClient();

  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [triggerType, setTriggerType] = useState<TriggerType>('phase_complete');
  const [channel, setChannel] = useState<Channel>('email');
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [phaseName, setPhaseName] = useState('');
  const [timingOffset, setTimingOffset] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const rulesQuery = useQuery({
    queryKey: ['automation-rules'],
    queryFn: listAutomationRules,
    staleTime: 30_000,
  });
  const templatesQuery = useQuery({
    queryKey: ['message-templates'],
    queryFn: listTemplates,
    staleTime: 30_000,
  });

  const createMutation = useMutation({
    mutationFn: () =>
      createAutomationRule({
        name: name.trim(),
        trigger_type: triggerType,
        channel,
        template_id: templateId as string,
        ...(phaseName.trim() ? { phase_name: phaseName.trim() } : {}),
        ...(timingOffset.trim() ? { timing_offset: timingOffset.trim() } : {}),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['automation-rules'] });
      setShowForm(false);
      setName('');
      setTemplateId(null);
      setPhaseName('');
      setTimingOffset('');
      setFormError(null);
    },
    onError: (err) => setFormError(extractErrorMessage(err, 'Could not create automation rule.')),
  });

  const onCreate = () => {
    setFormError(null);
    if (!name.trim()) {
      setFormError('Enter a rule name.');
      return;
    }
    if (!templateId) {
      setFormError('Select a template to send.');
      return;
    }
    createMutation.mutate();
  };

  const rules = rulesQuery.data ?? [];
  const templates = templatesQuery.data ?? [];
  const templateOptions = templates.map((t) => ({ value: t.id, label: t.name }));
  const templateName = (id: string) => templates.find((t) => t.id === id)?.name ?? id.slice(0, 8);

  return (
    <Screen scroll>
      <Text variant="title">Automations</Text>

      <Button
        title={showForm ? 'Cancel' : '+ New rule'}
        variant={showForm ? 'secondary' : 'primary'}
        onPress={() => setShowForm((v) => !v)}
        accessibilityLabel="New automation rule"
      />

      {showForm ? (
        <Card>
          <Text variant="bodyStrong">New rule</Text>
          <Field
            label="Name"
            placeholder="e.g. Notify after rough-in"
            value={name}
            onChangeText={setName}
            accessibilityLabel="Rule name"
          />
          <Select
            label="Trigger"
            options={TRIGGER_OPTIONS}
            value={triggerType}
            onChange={setTriggerType}
          />
          <Select label="Channel" options={CHANNEL_OPTIONS} value={channel} onChange={setChannel} />

          <Text variant="small" color="secondary">
            Template
          </Text>
          {templatesQuery.isLoading ? (
            <ActivityIndicator color={theme.color.action.primary} accessibilityLabel="Loading templates" />
          ) : templatesQuery.isError ? (
            <Text variant="small" color="danger">
              {extractErrorMessage(templatesQuery.error, 'Failed to load templates.')}
            </Text>
          ) : templateOptions.length === 0 ? (
            <Text variant="small" color="secondary">
              No templates yet. Create a template first.
            </Text>
          ) : (
            <Select
              options={templateOptions}
              value={templateId}
              onChange={setTemplateId}
            />
          )}

          <Field
            label="Phase name (optional)"
            placeholder="e.g. Rough-in"
            value={phaseName}
            onChangeText={setPhaseName}
            accessibilityLabel="Phase name"
          />
          <Field
            label="Timing offset (optional)"
            placeholder="e.g. 2d"
            value={timingOffset}
            onChangeText={setTimingOffset}
            autoCapitalize="none"
            accessibilityLabel="Timing offset"
          />
          {formError ? (
            <Text variant="bodyStrong" color="danger" accessibilityRole="alert">
              {formError}
            </Text>
          ) : null}
          <Button
            title="Save rule"
            onPress={onCreate}
            loading={createMutation.isPending}
            disabled={createMutation.isPending}
            accessibilityLabel="Save automation rule"
          />
        </Card>
      ) : null}

      <Text variant="label" color="secondary">
        {rules.length} {rules.length === 1 ? 'rule' : 'rules'}
      </Text>

      {rulesQuery.isLoading ? (
        <ActivityIndicator color={theme.color.action.primary} accessibilityLabel="Loading automation rules" />
      ) : rulesQuery.isError ? (
        <Card>
          <Text variant="body" color="danger" accessibilityRole="alert">
            {extractErrorMessage(rulesQuery.error, 'Failed to load automation rules.')}
          </Text>
          <Button
            title="Retry"
            variant="secondary"
            size="sm"
            fullWidth={false}
            onPress={() => rulesQuery.refetch()}
          />
        </Card>
      ) : rules.length === 0 ? (
        <Text variant="body" color="secondary">
          No automation rules yet. Tap “New rule” to add your first.
        </Text>
      ) : (
        <View style={styles.list}>
          {rules.map((r: AutomationRuleRow) => {
            const b = channelBadge(r.channel);
            const offset = r.timing_offset ? ` · ${r.timing_offset}` : '';
            const subtitle = `${humanize(r.trigger_type)} → ${templateName(r.template_id)}${offset}`;
            return (
              <ListRow
                key={r.id}
                title={r.name}
                subtitle={subtitle}
                right={<Badge status={b.key} label={b.label} />}
              />
            );
          })}
        </View>
      )}
    </Screen>
  );
}

const styles = StyleSheet.create({
  list: { gap: theme.spacing.sm },
});
